import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Alert, AlertDescription, AlertTitle } from '@factorialco/f0-react/dist/experimental'
import { ExternalLink } from '@factorialco/f0-react/icons/app'

import Sidepanel from 'modules/core/components/Sidepanel'
import i18n from 'modules/core/lib/i18n'

import { closeModal } from 'modules/benefits/components/benefitModals/actions'
import { useBenefitModals } from 'modules/benefits/components/benefitModals/useBenefitModals'

const WELLHUB_URL = 'https://account-manager.gympass.com/'

interface MetricCardProps {
  label: string
  value: string
}

interface AppCategoryListProps {
  title: string
  apps: string[]
}

const MetricCard = ({ label, value }: MetricCardProps) => (
  <Box
    borderRadius={{ all: 'abs012' }}
    border={{ all: { color: 'grey400', style: 'solid', width: 's1' } }}
    flexDirection="column"
    padding="s12"
    gap="s8"
    width="full"
    flexGrow
  >
    <Text.Normal size="200" color="grey1000">
      {label}
    </Text.Normal>

    <Text.Normal size="400" color="grey1000" weight="semibold">
      {value}
    </Text.Normal>
  </Box>
)

const AppCategoryList = ({ title, apps }: AppCategoryListProps) => (
  <Box gap="s8">
    <Text.Normal size="300" color="grey1000" weight="semibold">
      {title}
    </Text.Normal>

    <ul style={{ listStyleType: 'disc', paddingLeft: '16px' }}>
      {apps.map((app, index) => (
        <li key={index} style={{ marginBottom: '4px' }}>
          <Text.Normal size="300" color="grey1000">
            {app}
          </Text.Normal>
        </li>
      ))}
    </ul>
  </Box>
)

export const WellnessSidepanel = () => {
  const { dispatch } = useBenefitModals()

  const handleWellness = () => {
    window.open(WELLHUB_URL, '_blank')
    dispatch(closeModal())
  }

  return (
    <Sidepanel
      open
      onClose={() => dispatch({ type: 'CLOSE_MODAL' })}
      title={i18n.t('benefits.homepage.static_benefits.wellness.title')}
      closeLabel={i18n.t('common.close')}
      width="wide"
      footer={
        <F0Button
          onClick={handleWellness}
          size="md"
          variant="default"
          label={i18n.t('benefits.homepage.static_benefits.wellness.cta')}
          icon={ExternalLink}
        />
      }
    >
      <Box gap="s2" paddingBottom="s24">
        <Box gap="s8">
          <Text.Normal size="400" color="grey1000" weight="semibold">
            {i18n.t('benefits.homepage.static_benefits.wellness.subtitle')}
          </Text.Normal>

          <Text.Normal size="300" color="grey800">
            {i18n.t('benefits.homepage.static_benefits.wellness.description')}
          </Text.Normal>
        </Box>
      </Box>

      <Box flexDirection="row" gap="s8" width="full" paddingBottom="s24">
        <MetricCard
          label={i18n.t('benefits.homepage.static_benefits.wellness.total_cost')}
          value={i18n.t('benefits.homepage.static_benefits.wellness.free')}
        />
        <MetricCard
          label={i18n.t('benefits.homepage.static_benefits.wellness.apps_included')}
          value={i18n.t('benefits.homepage.static_benefits.wellness.apps_included_value')}
        />
        <MetricCard
          label={i18n.t('benefits.homepage.static_benefits.wellness.live_classes')}
          value={i18n.t('benefits.homepage.static_benefits.wellness.live_classes_value')}
        />
      </Box>

      <Alert variant="info">
        <AlertTitle>
          {i18n.t('benefits.homepage.static_benefits.wellness.more_options_available')}
        </AlertTitle>
        <AlertDescription>
          {i18n.t('benefits.homepage.static_benefits.wellness.upgrade_plan')}
        </AlertDescription>
      </Alert>

      <Box gap="s12" paddingTop="s24">
        <AppCategoryList
          title={i18n.t('benefits.homepage.static_benefits.wellness.apps_list.fitness')}
          apps={[
            'Fizzup',
            'Instafit',
            'Gym Life',
            i18n.t('benefits.homepage.static_benefits.wellness.more_options_available_value'),
          ]}
        />

        <AppCategoryList
          title={i18n.t('benefits.homepage.static_benefits.wellness.apps_list.mindfulness')}
          apps={[
            'iFeel',
            'Sleep Cycle',
            'Meditopia',
            i18n.t('benefits.homepage.static_benefits.wellness.more_options_available_value'),
          ]}
        />

        <AppCategoryList
          title={i18n.t('benefits.homepage.static_benefits.wellness.apps_list.nutrition')}
          apps={[
            'Myfitenesspal',
            'Faboulous',
            'Maya',
            'Mobilis',
            i18n.t('benefits.homepage.static_benefits.wellness.more_options_available_value'),
          ]}
        />
      </Box>
    </Sidepanel>
  )
}
